import { Component, DestroyRef, HostBinding, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PageEvent } from '@angular/material/paginator';
import { Evaluation } from '@app/models/enseignant';
import { PermissionService, PlatformService, UserService } from '@app/services';
import { EnseignantService } from '@app/services/enseignant.service';
import { distinctUntilChanged, startWith } from 'rxjs';

@Component({
  selector: 'app-list-evaluation',
  templateUrl: './list-evaluation.component.html',
  styleUrl: './list-evaluation.component.scss',
  standalone: false
})
export class ListEvaluationComponent implements OnInit {

  @HostBinding('class.height-full-minus-header-subheader') hostClass = false;
  @HostBinding('class.height-full-minus-header') hostClassForMobile = false;

  enseignantService = inject(EnseignantService);
  userService = inject(UserService);
  platformService = inject(PlatformService);
  destroyRef = inject(DestroyRef);
  permService = inject(PermissionService);

  evaluationsList: Evaluation[];        // Toutes les données
  filteredEvaluations: Evaluation[] = [];    // Données après filtre

  // Filtres actifs
  libelleFilter = '';
  numFilter = '';
  coursFilter = '';
  disciplineFilter = '';
  startDate: Date | null = null;
  endDate: Date | null = null;

  // Pagination
  pageSize = 12;
  totalItems = 0;
  pageIndex = 0;
  pageSizeOptions = [12, 24, 48];

  idEnseignant: number;
  filterSettings = { opened: false, mode: 'over', width: 150 };
  isMobile: boolean = false;
  tableView: boolean = false; // false = cartes, true = tableau
  permToCreate: boolean = false;

  ngOnInit(): void {
    this.idEnseignant = this.userService.currentUser.idEnseignant;
    this.permToCreate = this.permService.hasPermission('enseignant_evaluation_create');

    this.enseignantService.getAllEvaluationByIdEnseignant(this.idEnseignant)
      .subscribe(evaluations => {
        this.evaluationsList = evaluations;
        this.filteredEvaluations = [...evaluations];
        this.totalItems = evaluations.length;
      });

    this.platformService.isMobile$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged())
      .subscribe((isMobile) => {
        this.hostClassForMobile = isMobile;
        this.hostClass = !isMobile;
        this.isMobile = isMobile;
        this.tableView = false;
      });

    this.platformService.mainWidth$
      .pipe(
        startWith(this.platformService.mainWidth()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(mainWidth => {
        const isOver = mainWidth < 1200;
        this.filterSettings.mode = isOver ? 'over' : 'side';
      });
  }

  deleteEvaluation(id: number) {
    this.evaluationsList = this.evaluationsList.filter(e => e.idEvaluation !== id);
    this.filteredEvaluations = this.filteredEvaluations.filter(e => e.idEvaluation !== id);
    this.totalItems = this.filteredEvaluations.length;
  }

  onLibelleFilterChange(event: any): void {
    this.libelleFilter = event.target.value?.toLowerCase() || '';
    this.applyFilters();
  }

  onNumFilterChange(event: any): void {
    this.numFilter = event.target.value?.toLowerCase() || '';
    this.applyFilters();
  }

  onCoursFilterChange(event: any): void {
    this.coursFilter = event.target.value?.toLowerCase() || '';
    this.applyFilters();
  }

  onDisciplineFilterChange(event: any): void {
    this.disciplineFilter = event.target.value?.toLowerCase() || '';
    this.applyFilters();
  }

  onDateRangeFilterChange(): void {
    this.applyFilters();
  }

  applyFilters(): void {
    this.filteredEvaluations = this.evaluationsList.filter(data => {
      const libelleMatch = !this.libelleFilter || data.libelle?.toLowerCase().includes(this.libelleFilter.toLowerCase());
      const numMatch = !this.numFilter || data.numEvaluation?.toString().toLowerCase().includes(this.numFilter.toLowerCase());
      const coursMatch = !this.coursFilter || data?.cours?.libelle?.toLowerCase().includes(this.coursFilter.toLowerCase());
      const disciplineMatch = !this.disciplineFilter || data?.discipline?.libelle?.toLowerCase().includes(this.disciplineFilter.toLowerCase());

      const dateDebut = new Date(data.dateDebut);
      const startDateMatch = !this.startDate || dateDebut >= this.startDate;
      const endDateMatch = !this.endDate || dateDebut <= this.endDate;

      return libelleMatch && numMatch && coursMatch && disciplineMatch && startDateMatch && endDateMatch;
    });

    this.totalItems = this.filteredEvaluations.length;
    this.pageIndex = 0; // retour à la première page
  }

  hasActiveFilters(): boolean {
    return !!(this.libelleFilter || this.numFilter || this.coursFilter || this.disciplineFilter || this.startDate || this.endDate);
  }

  resetFilters(): void {
    this.libelleFilter = '';
    this.numFilter = '';
    this.coursFilter = '';
    this.disciplineFilter = '';
    this.startDate = null;
    this.endDate = null;
    this.applyFilters();
  }

  pageChange(data: PageEvent) {
    this.pageSize = data.pageSize;
    this.pageIndex = data.pageIndex;
  }

  closeDrawer() {
    this.filterSettings.opened = false;
  }

  toggleOptions() {
    this.filterSettings.opened = !this.filterSettings.opened;
  }
}
